// Underline-style route tabs: left-aligned, thin bottom rule, active tab in
// brand green with a 2px underline. `children` render on the right of the row
// (e.g. a status pill). Overflows with horizontal scroll rather than a select.

import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { When } from 'react-if';
import { Tooltip } from '@mui/material';

import { classNames } from '../lib/classNames';

import { ITab, useCurrentTabName } from './Tabs';

interface IUnderlineTabsProps {
  tabs: ITab[];
  navigational?: boolean;
  className?: string;
  children?: ReactNode;
  /** No side padding on tabs, so the first tab lines up with the content above and the underline matches the label */
  dense?: boolean;
}

const badgeColors: Record<string, string> = {
  gray: `bg-gray-100 text-gray-700`,
  green: `bg-green-100 text-green-700`,
  purple: `bg-purple-100 text-purple-700`,
  orange: `bg-orange-100 text-orange-700`,
  red: `bg-red-100 text-red-700`,
};

function TabBadge({ text, className, tooltip }: { text: string; className: string; tooltip?: string }) {
  return (
    <Tooltip
      disableHoverListener={ !tooltip }
      title={ tooltip }
    >
      <span className={ classNames(`ml-1.5 rounded-full px-2 py-px text-[11px] font-medium`, className) }>
        { text }
      </span>
    </Tooltip>
  );
}

export function UnderlineTabs({ tabs, navigational = true, className, children, dense }: IUnderlineTabsProps) {
  const currentTab = useCurrentTabName(tabs, navigational);

  return (
    <div className={ classNames(`flex items-center gap-3 border-b border-gray-200`, className) }>
      <nav
        className={ classNames(`-mb-px flex min-w-0 flex-1 overflow-x-auto`, dense && `gap-7`) }
        aria-label={ `Tabs` }
      >
        { tabs.map(tab => {
          const current = tab.name === currentTab || tab.current;

          const el = (
            <Link
              key={ tab.name }
              to={ tab.href }
              onClick={ e => {
                if (tab.onClick) {
                  e.preventDefault();
                  tab.onClick();
                }
              } }
              className={ classNames(
                `inline-flex flex-none items-center whitespace-nowrap border-b-2 text-sm`,
                dense ? `px-0 pb-2.5 pt-0` : `px-4 py-3`,
                current
                  ? classNames(`border-paidniceGreen-600 text-paidniceGreen-600`, dense ? `font-medium` : `font-semibold`)
                  : classNames(`border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700`, dense ? `font-normal` : `font-medium`),
              ) }
              aria-current={ current ? `page` : undefined }
            >
              <When condition={ !!tab.icon }>
                { () => (
                  <tab.icon
                    className={ classNames(
                      current ? `text-paidniceGreen-500` : `text-gray-400`,
                      `-ml-0.5 mr-2 h-4 w-4`,
                    ) }
                    aria-hidden={ `true` }
                  />
                ) }
              </When>
              { tab.name }
              <When condition={ !!tab.badge }>
                { () => (
                  <TabBadge
                    text={ tab.badge.text }
                    tooltip={ tab.badge.tooltip }
                    className={ badgeColors[tab.badge.color] || badgeColors.gray }
                  />
                ) }
              </When>
              <When condition={ !!tab.secondaryBadge }>
                { () => (
                  <TabBadge
                    text={ tab.secondaryBadge.text }
                    tooltip={ tab.secondaryBadge.tooltip }
                    className={ tab.secondaryBadge.className || `bg-gray-100 text-gray-700` }
                  />
                ) }
              </When>
              <When condition={ tab.new }>
                <TabBadge
                  text={ `NEW` }
                  className={ badgeColors.purple }
                />
              </When>
            </Link>
          );

          if (tab.tooltip) {
            return (
              <Tooltip
                key={ tab.name }
                title={ tab.tooltip }
              >
                { el }
              </Tooltip>
            );
          }

          return el;
        }) }
      </nav>
      <When condition={ !!children }>
        <div className={ `flex-none pb-2` }>{ children }</div>
      </When>
    </div>
  );
}
